/**
 * Brand tokens a screen can lay over the kit's own: the colors `NowadaysUiScope` writes on its
 * wrapper and on its overlay container so every kit part inside (a picked tile, a primary button,
 * a focus ring, a select popover) takes them. The kit only checks the values; working out a
 * readable pair from a tenant's palette is the caller's job.
 */

import type { CSSProperties } from "react";

/** The colors a screen hands the kit. Hex only, `#rrggbb`. */
export interface KitBrand {
  /** Fills and accents: the picked tile, the primary button, a checked box. */
  primary: string;
  /** Text and marks drawn on `primary`. Pick it by contrast with `primary`. */
  primaryForeground: string;
  /** Focus rings. Defaults to `primary`. */
  ring?: string;
}

/** The custom properties a brand sets, by name. Also a valid `style` value. */
export type KitBrandProperties = CSSProperties & Record<(typeof KIT_BRAND_PROPERTY_NAMES)[number], string>;

/** The kit tokens a brand replaces, in the order they are written. */
export const KIT_BRAND_PROPERTY_NAMES = ["--primary", "--primary-foreground", "--ring"] as const;

/** A six-digit hex color. Anything else (a name, rgb(), a var(), a url) is refused. */
const KIT_BRAND_HEX = /^#[0-9a-f]{6}$/i;

/**
 * @description Turns a brand into the custom properties the kit reads. A brand with any value
 *   that is not a six-digit hex color is dropped whole, so the kit falls back to its own colors
 *   rather than mixing a tenant fill with a Nowadays foreground.
 * @param brand - The brand, or null or undefined for none.
 * @returns The properties to set, or null to leave the kit as it is.
 */
export function kitBrandProperties(brand: KitBrand | null | undefined): KitBrandProperties | null {
  if (!brand) return null;
  const ring = brand.ring ?? brand.primary;
  const values = [brand.primary, brand.primaryForeground, ring];
  if (!values.every((value) => typeof value === "string" && KIT_BRAND_HEX.test(value))) return null;
  return { "--primary": brand.primary, "--primary-foreground": brand.primaryForeground, "--ring": ring };
}

/**
 * @description Writes a brand's properties on an element, or clears them when there is none.
 * @param element - The element to write on (the overlay container).
 * @param properties - The properties, or null to clear them.
 * @returns Nothing.
 */
export function applyKitBrand(element: HTMLElement, properties: KitBrandProperties | null): void {
  for (const name of KIT_BRAND_PROPERTY_NAMES) {
    if (properties) element.style.setProperty(name, properties[name]);
    else element.style.removeProperty(name);
  }
}
